/**
 * Generic single-order detail upsert for Postgres-backed tables.
 * @module singleOrderDetailsGenericUpsert
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for upserting a single order's details into a specific Postgres table. */
export interface SingleOrderDetailsGenericUpsertParams<T> {
  order_id: string;
  org_id: string;
  /** Target table/resource to upsert (e.g. clinical-document). */
  table_name: SingleOrderDetailsTableName;
  /** Data payload to insert or update on the target table. */
  data: T;
}

/** Response shape returned by the Postgres upsert endpoint. */
export interface SingleOrderDetailsGenericUpsertResponse {
  success: boolean;
  updated_rows: number;
}

/**
 * Upserts details for a single medical PA order into a specified Postgres table.
 *
 * @typeParam T - Shape of the upsert payload sent in the request body.
 * @param params - Order ID, org ID, target table name, and the data to upsert.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The upsert result indicating success and number of affected rows.
 * @throws {Error} When the HTTP response is not OK (includes response body in the message).
 * @throws Re-throws any network or parsing error after logging.
 */
export const singleOrderDetailsGenericUpsert = async <T>(
  params: SingleOrderDetailsGenericUpsertParams<T>,
  signal?: AbortSignal,
): Promise<SingleOrderDetailsGenericUpsertResponse> => {
  try {
    const url = `${API_ENDPOINTS.SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_UPSERT}/${params.table_name}`;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
      data: params.data,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: SingleOrderDetailsGenericUpsertResponse =
      await response.json();
    if (!response.ok) {
      throw new Error(
        `Postgres upsert request failed (${response.status}): ${JSON.stringify(result)}`,
      );
    }
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
